import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import type { MaterialType, CreateMaterialConsumption } from '../../types/material';
import { materialTypesApi, materialConsumptionApi } from '../../api/materialApi';
import { useToast } from '../ui/use-toast';

interface MaterialConsumptionFormProps {
  workEntryId: number;
  addedByUserId: number;  // Who is adding the material (supervisor)
  addedForUserId: number; // Who the material is for (worker)
  onSuccess?: (newConsumption: import('../../types/material').MaterialConsumption) => void;
  onCancel?: () => void;
}

export function MaterialConsumptionForm({ 
  workEntryId, 
  addedByUserId,
  addedForUserId, 
  onSuccess, 
  onCancel 
}: MaterialConsumptionFormProps) {
  const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
  const [selectedMaterialType, setSelectedMaterialType] = useState<MaterialType | null>(null);
  const [formData, setFormData] = useState<Omit<CreateMaterialConsumption, 'workEntryId' | 'addedForUserId'>>({
    materialTypeId: null,
    quantity: 0,
    unitId: null,
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(true);
  const [isCustomMaterial, setIsCustomMaterial] = useState(false);
  const [customMaterialName, setCustomMaterialName] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const loadMaterialTypes = async () => {
      try {
        const types = await materialTypesApi.getAll();
        setMaterialTypes(types);
      } catch (error) {
        console.error('Error loading material types:', error);
        toast({
          title: 'Eroare',
          description: 'Nu s-au putut încărca tipurile de materiale',
          variant: 'destructive'
        });
      } finally {
        setIsLoadingMaterials(false);
      }
    };

    loadMaterialTypes();
  }, [toast]);

  const handleMaterialTypeChange = (materialTypeId: string) => {
    if (materialTypeId === 'custom') {
      setIsCustomMaterial(true);
      setSelectedMaterialType(null);
      setFormData(prev => ({
        ...prev,
        materialTypeId: null, // Use null to indicate custom material
        unitId: null // Custom unit
      }));
    } else {
      setIsCustomMaterial(false);
      setCustomMaterialName('');
      setCustomUnit('');
      const selectedType = materialTypes.find(mt => mt.id === parseInt(materialTypeId));
      setSelectedMaterialType(selectedType || null);
      
      // Set default unit if available
      if (selectedType && selectedType.units.length > 0) {
        const defaultUnit = selectedType.units.find(u => u.symbol === selectedType.defaultUnit) || selectedType.units[0];
        setFormData(prev => ({
          ...prev,
          materialTypeId: selectedType.id,
          unitId: defaultUnit.id
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          materialTypeId: parseInt(materialTypeId),
          unitId: 0
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isCustomMaterial) {
      if (!customMaterialName.trim() || !customUnit.trim() || formData.quantity <= 0) {
        toast({
          title: 'Eroare',
          description: 'Toate câmpurile sunt obligatorii pentru material personalizat',
          variant: 'destructive'
        });
        return;
      }
    } else if (formData.materialTypeId === null || formData.unitId === null || formData.quantity <= 0) {
      toast({
        title: 'Eroare',
        description: 'Toate câmpurile sunt obligatorii și cantitatea trebuie să fie mai mare ca 0',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    
    // Log the data being sent
    const consumptionData = {
      ...formData,
      workEntryId,
      addedByUserId,
      addedForUserId
    };
    
    // If custom material, add the custom name and unit to notes
    if (isCustomMaterial) {
      consumptionData.notes = `Material personalizat: ${customMaterialName} (${formData.quantity} ${customUnit})${formData.notes ? '. ' + formData.notes : ''}`;
    }
    
    console.log('Creating material consumption with data:', consumptionData);
    
    try {
      const newConsumption = await materialConsumptionApi.create(consumptionData);

      toast({
        title: 'Succes',
        description: 'Consumul de material a fost înregistrat cu succes'
      });

      // Reset form
      setFormData({
        materialTypeId: null,
        quantity: 0,
        unitId: null,
        notes: ''
      });
      setSelectedMaterialType(null);
      setIsCustomMaterial(false);
      setCustomMaterialName('');
      setCustomUnit('');
      
      // Pass the new consumption to parent for optimistic update
      onSuccess?.(newConsumption);
    } catch (error: unknown) {
      console.error('Error creating material consumption:', error);
      
      // Extract error message from API response
      let errorMessage = 'Nu s-a putut înregistra consumul de material';
      if (error.message) {
        if (error.message.includes('sesiune încheiată')) {
          errorMessage = 'Nu se pot adăuga materiale la o sesiune încheiată. Verificați dacă membrul este încă la lucru.';
        } else if (error.message.includes('utilizatori nu există')) {
          errorMessage = 'Utilizatorul specificat nu există. Vă rugăm să reîncărcați pagina.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: 'Eroare',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingMaterials) {
    return <div className="text-center py-4">Se încarcă tipurile de materiale...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="material-type">Tip Material *</Label>
        <Select onValueChange={handleMaterialTypeChange} value={isCustomMaterial ? 'custom' : (formData.materialTypeId?.toString() || '')}>
          <SelectTrigger>
            <SelectValue placeholder="Selectează tipul de material" />
          </SelectTrigger>
          <SelectContent>
            {materialTypes.map((type) => (
              <SelectItem key={type.id} value={type.id.toString()}>
                {type.name} {type.category && `(${type.category})`}
              </SelectItem>
            ))}
            <SelectItem value="custom" className="font-medium text-blue-600">
              ➕ Alt material (personalizat)
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isCustomMaterial && (
        <>
          <div className="space-y-2">
            <Label htmlFor="custom-material">Nume Material Personalizat *</Label>
            <Input
              id="custom-material"
              type="text"
              value={customMaterialName}
              onChange={(e) => setCustomMaterialName(e.target.value)}
              placeholder="Ex: Șuruburi speciale, Adeziv X, etc."
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="custom-unit">Unitate de Măsură *</Label>
            <Input
              id="custom-unit"
              type="text"
              value={customUnit}
              onChange={(e) => setCustomUnit(e.target.value)}
              placeholder="Ex: bucăți, kg, litri, metri, etc."
            />
          </div>
        </>
      )}

      {selectedMaterialType && !isCustomMaterial && (
        <div className="space-y-2">
          <Label htmlFor="unit">Unitate de Măsură *</Label>
          <Select onValueChange={(value) => setFormData(prev => ({ ...prev, unitId: parseInt(value) }))} value={formData.unitId?.toString() || ''}>
            <SelectTrigger>
              <SelectValue placeholder="Selectează unitatea" />
            </SelectTrigger>
            <SelectContent>
              {selectedMaterialType.units.map((unit) => (
                <SelectItem key={unit.id} value={unit.id.toString()}>
                  {unit.name} {unit.symbol && `(${unit.symbol})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="quantity">Cantitate *</Label>
        <Input
          id="quantity"
          type="number"
          step="0.01"
          min="0.01"
          value={formData.quantity || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
          placeholder="Introdu cantitatea"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Observații</Label>
        <Textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Observații opționale despre consumul de material"
          rows={3}
        />
      </div>

      <div className="flex gap-2 pt-4">
        <Button 
          type="submit" 
          disabled={isLoading || (!isCustomMaterial && (formData.materialTypeId === null || formData.unitId === null)) || formData.quantity <= 0 || (isCustomMaterial && (!customMaterialName.trim() || !customUnit.trim()))}
          className="flex-1"
        >
          {isLoading ? 'Se salvează...' : 'Adaugă Consum'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Anulează
          </Button>
        )}
      </div>
    </form>
  );
}